import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * FERMETURE D'UN POPOVER ANCRÉ (menu, panneau attaché à son déclencheur).
 *
 * Le projet répétait le même trio dans chaque menu (`SortMenu`, `Menu`, `ToolsMenu`,
 * `OverflowMenu`, `SavedFiltersMenu`, `AccountSwitcher`, `NotificationBell`) : état
 * `open`, écouteur `pointerdown` document pour le clic extérieur, écouteur `keydown`
 * pour `Échap`. Ce hook en est le noyau commun, afin qu'un nouveau popover n'ait pas
 * à réinventer — ni à oublier — l'une des trois pièces.
 *
 * À NE PAS CONFONDRE avec `useScrimDismiss`, qui traite un problème DIFFÉRENT : celui
 * du VOILE plein écran d'une modale, où un `click` naît sur l'ancêtre commun du
 * `pointerdown` et du `pointerup` (voir le commentaire de ce fichier). Ici il n'y a
 * pas de voile : le popover est ancré, et « extérieur » se décide par rapport à la
 * RACINE du composant — un geste de sélection de texte commencé dans le panneau et
 * relâché dehors ne ferme donc jamais (le `pointerdown` était à l'intérieur).
 *
 * RETOUR DU FOCUS : `Échap` rend le focus au déclencheur (l'utilisateur au clavier ne
 * doit pas se retrouver au début du document) ; un clic extérieur ne le rend PAS —
 * le pointeur a déjà désigné où l'attention va.
 *
 * SE COMPOSE AVEC `usePopoverClamp`, l'autre moitié du sujet : ce hook-ci décide QUAND
 * le panneau disparaît, celui-là qu'il reste DANS le cadre visible tant qu'il est là.
 */
export interface PopoverDismiss<Root extends HTMLElement, Trigger extends HTMLElement> {
  /** Le popover est-il déployé ? */
  open: boolean;
  /** À poser sur la racine du composant : délimite l'« intérieur » du clic extérieur. */
  rootRef: React.RefObject<Root | null>;
  /** À poser sur le déclencheur : cible du retour de focus. */
  triggerRef: React.RefObject<Trigger | null>;
  /** Bascule (clic sur le déclencheur). */
  toggle: () => void;
  /** Ouvre sans basculer (ouverture provoquée par une autre action). */
  openPopover: () => void;
  /** Ferme ; rend le focus au déclencheur sauf si `returnFocus` vaut `false`. */
  close: (returnFocus?: boolean) => void;
}

export function usePopoverDismiss<
  Root extends HTMLElement = HTMLDivElement,
  Trigger extends HTMLElement = HTMLButtonElement,
>(): PopoverDismiss<Root, Trigger> {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<Root>(null);
  const triggerRef = useRef<Trigger>(null);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const openPopover = useCallback(() => setOpen(true), []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  return { open, rootRef, triggerRef, toggle, openPopover, close };
}
